import type { SortDescriptor } from "react-stately";

import { useEffect, useState } from "react";

import { useDebounce } from "@/hooks/useDebounce";
import { validatePageSize } from "@/utils/paginationHelpers";

interface UsePaginationOptions {
  defaultItemsPerPage?: number;
  defaultSortColumn?: string;
  defaultSortDirection?: "ascending" | "descending";
  searchDebounceMs?: number;
}

interface PaginationMetadata {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

export interface UsePaginationReturn {
  debouncedQuery: string;
  inputValue: string;
  itemsPerPage: number;
  page: number;
  setInputValue: (value: string) => void;
  setItemsPerPage: (value: number) => void;
  setPage: (value: number) => void;
  setSortDescriptor: (descriptor: SortDescriptor) => void;
  sortDescriptor: SortDescriptor;
  updateMetadata: (metadata?: PaginationMetadata) => void;
}

/**
 * Custom hook for managing pagination with dynamic page size adjustment
 * Automatically adjusts page size when it's not available in the current data set
 */
export function usePaginationWithDynamicPageSize(
  initialMetadata?: PaginationMetadata,
  options: UsePaginationOptions = {},
): UsePaginationReturn {
  const {
    defaultItemsPerPage = 10,
    defaultSortColumn = "createdAt",
    defaultSortDirection = "descending",
    searchDebounceMs = 500,
  } = options;

  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: defaultSortColumn,
    direction: defaultSortDirection,
  });
  const [metadata, setMetadata] = useState<PaginationMetadata | undefined>(
    initialMetadata,
  );

  const debouncedQuery = useDebounce(inputValue, searchDebounceMs, () =>
    setPage(1),
  );

  const updateMetadata = (newMetadata?: PaginationMetadata) => {
    setMetadata(newMetadata);
  };

  // Adjust itemsPerPage when metadata changes (tab switching, data loading)
  useEffect(() => {
    if (metadata?.totalItems !== undefined) {
      const { shouldUpdate, validPageSize } = validatePageSize(
        itemsPerPage,
        metadata.totalItems,
      );

      if (shouldUpdate) {
        setItemsPerPage(validPageSize);
        setPage(1);
      }
    }
  }, [metadata?.totalItems, itemsPerPage]);

  return {
    debouncedQuery,
    inputValue,
    itemsPerPage,
    page,
    setInputValue,
    setItemsPerPage,
    setPage,
    setSortDescriptor,
    sortDescriptor,
    updateMetadata,
  };
}
